/**
 * What's New — INVENTORY-DESKTOP §4 row 7 (lane D4). Prototype lines 1078-1148.
 * Mounted by `DesktopApp` while `s.wnOpen`.
 *
 * The queue is a snapshot taken when the modal opens (`ui.openWhatsNew`), not a
 * live derivation — `newForMe` drops an entry the instant its vote lands, so a
 * live queue would renumber itself mid-review. This modal only advances `wnIdx`.
 *
 * THE CREW BAND IS GONE (2026-08-25). The crew's calls are avatars in the
 * column under the button each member picked — `components/CallColumns.tsx`,
 * fed by `whatsNewVM.callCols` — and the only words left are `N OF M CALLED`
 * beside the band heading. Detail keeps its own labelled `voteGroups`: that
 * panel is the record, this is a glance before you place your own call.
 *
 * DESKTOP LAYOUT (§4)
 *   640px, `max-height:90vh` — the ONE modal that is not 92vh.
 *   FOUR FULL-BLEED BANDS: media → the game → YOUR CALL (strip + the crew's
 *   calls under it) → THE NUMBERS. The 640 modal shows every band without
 *   scrolling, so this order is parity with the 430 sheet rather than a fix for
 *   the same problem — but two trees with one card should not read as two
 *   designs.
 *   The link out lives on the byline row as a 30px `STEAM ↗` chip
 *   (`components/SteamChip.tsx`); the YOUR CALL band keeps only `SKIP →`,
 *   right-anchored beside its own heading.
 *
 * That media block is `components/MediaCarousel.tsx` (PORTING-NOTES P10-01), not
 * §4's `listType=search` `<iframe>`, which is dead upstream. It takes `desktop`
 * like the Detail panel does — same tree, same absent swipe, so its thumb strip
 * needs the same ‹ › nudges (P10-02) — and it owns the `SEARCH YOUTUBE ↗`
 * fallback the embed used to carry beneath it. It also calls `ensureMedia`
 * itself, which this surface needs: What's New never runs `openGame`, so nothing
 * else here would fetch the Steam half.
 *
 * CUSTOM HEADER, not `Modal`'s `header` prop: the prototype puts `CLEAR ALL`
 * immediately after the progress note with the spacer AFTER it, and both it and
 * the ✕ carry tooltips. `ModalHeader` places its `extra` against the ✕ and
 * renders an untipped close, so this composes `ModalClose` instead — which is
 * what D0 exported it for. Geometry is `ModalHeader`'s, unchanged.
 *
 * Bylines use `format.ts#ago(ts, now)` through `whatsNewVM.agoTxt`. The
 * prototype renders `JUST NOW` here for every card because its zero-arg `ago()`
 * shadows `ago(ts)` (§9.1) — that is a bug, not a spec, and is not reproduced.
 */

import { Avatar } from '../../components/Avatar.tsx';
import { CallColumns } from '../../components/CallColumns.tsx';
import { MediaCarousel } from '../../components/MediaCarousel.tsx';
import { NumbersBand } from '../../components/NumbersBand.tsx';
import { SteamChip } from '../../components/SteamChip.tsx';
import { useArt } from '../../components/useArt.ts';
import { VoteButtons } from '../../components/VoteButtons.tsx';
import type { GameId } from '../../lib/types.ts';
import { useQd } from '../../store/index.ts';
import { WN_CLEAR_TIP, WN_CLOSE_TIP } from '../../store/selectors.d4.ts';
import { whatsNewVM } from '../../store/selectors.ts';
import { Modal, ModalClose } from '../components/Modal.tsx';
import type { CSSProperties } from 'react';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

type WnVM = ReturnType<typeof whatsNewVM>;
/** The populated branch — `hasCard` is a plain boolean, so this needs a
 *  structural guard rather than a discriminant. */
type WnCard = Extract<WnVM, { currentId: GameId }>;
const isCard = (v: WnVM): v is WnCard => 'currentId' in v;

/** `SKIP →`, right-anchored in the YOUR CALL header row (2026-08-25). The
 *  padding is the tap/click target, and matches the sheet's. */
const SKIP: CSSProperties = {
  fontFamily: MONO,
  fontSize: '10.5px',
  color: 'var(--gt-text-5)',
  cursor: 'pointer',
  padding: '8px 9px',
};

export default function WhatsNewModal(): JSX.Element {
  const s = useQd();
  const vm = whatsNewVM(s);
  const card: WnCard | null = isCard(vm) ? vm : null;
  const art = useArt((card ? card.currentId : '') + 'p', card ? card.coverCands : []);
  const close = () => s.closeWhatsNew();

  /* The prototype's own header row: title · progress · CLEAR ALL · spacer · ✕.
     Title, colour and whether CLEAR ALL renders all come from the VM — a call
     run is the same card in a different voice. */
  const head = (progress: string, hdrTxt: string, hdrClr: string, showClear: boolean) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '15px 18px',
        borderBottom: '1px solid var(--gt-line)',
        background: 'var(--gt-card)',
        position: 'sticky',
        top: 0,
        zIndex: 1,
        flex: 'none',
      }}
    >
      <div
        style={{
          fontFamily: PIXEL,
          fontSize: '9.5px',
          color: hdrClr,
          transform: 'translateY(0.13em)',
          whiteSpace: 'nowrap',
        }}
      >
        {hdrTxt}
      </div>
      <div style={{ fontFamily: MONO, fontSize: '10.5px', color: 'var(--gt-muted)' }}>{progress}</div>
      {showClear ? (
        <div
          onClick={() => void s.wnClearAll()}
          data-tip={WN_CLEAR_TIP}
          style={{
            fontFamily: MONO,
            fontSize: '9.5px',
            color: 'var(--gt-text-5)',
            border: '1px solid var(--gt-border-2)',
            padding: '3px 8px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          CLEAR ALL
        </div>
      ) : null}
      <div style={{ flex: 1 }} />
      <ModalClose onClose={close} tip={WN_CLOSE_TIP} />
    </div>
  );

  if (!card) {
    // The queue emptied under us (a realtime patch, or another tab). Render the
    // chrome only; the ◆ pill is what re-opens with a fresh queue.
    return (
      <Modal label="What's new modal" width={640} maxHeight="90vh" onClose={close}>
        {head('0 MORE', vm.hdrTxt, vm.hdrClr, vm.showClear)}
        <div style={{ padding: '40px 18px', textAlign: 'center', fontFamily: MONO, fontSize: 11, color: 'var(--gt-muted)' }}>
          {vm.emptyTxt}
        </div>
      </Modal>
    );
  }

  return (
    <Modal label="What's new modal" width={640} maxHeight="90vh" onClose={close}>
      {head(card.progressTxt, card.hdrTxt, card.hdrClr, card.showClear)}

      {/* BAND 1 · MEDIA — edge to edge, flush to the card border ----------- */}
      <MediaCarousel gameId={card.currentId} desktop noFoot />

      {/* BAND 2 · THE GAME ------------------------------------------------- */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '14px 18px 16px', background: 'var(--gt-card)' }}>
        <div style={{ display: 'flex', gap: 14 }}>
          {/* the 64px cover is hand-inlined in the prototype, not Cover Art —
              no checker texture, initials at 10px */}
          <div
            style={{
              position: 'relative',
              width: 64,
              aspectRatio: '2/3',
              alignSelf: 'flex-start',
              background: card.fall,
              overflow: 'hidden',
              border: '1px solid var(--gt-border)',
              flex: 'none',
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: PIXEL,
                fontSize: 10,
                color: 'rgba(255,255,255,.25)',
              }}
            >
              {card.ini}
            </div>
            {art.hasCover ? <div style={{ position: 'absolute', inset: 0, background: art.cover }} /> : null}
          </div>

          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
            {/* The byline row anchors the STEAM chip (2026-08-25). It must not
                wrap: the chip is positioned by the spacer, and a wrapped row
                would drop it onto a line of its own. The byline ellipsizes
                instead — a long name loses characters, not the chip. */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontFamily: MONO,
                fontSize: 10,
                color: 'var(--gt-text-5)',
              }}
            >
              <Avatar
                style={{ flex: 'none' }}
                size={20}
                fs={6}
                ini={card.byIni}
                bg={card.byHueBg}
                hasAv={card.byHasAv}
                avBg={card.byAvBg}
              />
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                ADDED BY {card.byTxt} · {card.agoTxt}
              </span>
              <div style={{ flex: 1 }} />
              <SteamChip href={card.steamUrl} hover />
            </div>

            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.2, color: '#fff' }}>{card.name}</div>
              <div style={{ fontFamily: MONO, fontSize: '10.5px', color: 'var(--gt-text-5)' }}>{card.dev}</div>
            </div>

            {/* No meta line and no review chip here any more: price, players
                and the review score are THE NUMBERS' job since 2026-08-24, and
                stating them twice in two vocabularies is how the two came to
                disagree. `metaTxt` / `revTxt` stay on the view model, as the
                design parks them. */}
            <div style={{ fontSize: '12.5px', lineHeight: 1.6, color: 'var(--gt-text-3)', textWrap: 'pretty' }}>
              {card.descTxt}
            </div>
          </div>
        </div>
      </div>

      {/* BAND 3 · YOUR CALL — above the numbers, matching the sheet ---------- */}
      {/* The strip is the point of the card, so it comes before the reading
          material rather than after it (2026-08-25). The 640 modal shows every
          band without scrolling either way; this is parity with the 430 sheet,
          where the order was the difference between calling and scrolling. */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 11,
          padding: '16px 18px',
          background: 'var(--gt-raised)',
          borderTop: '1px solid var(--gt-line)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, minHeight: 26 }}>
          <span style={{ fontFamily: PIXEL, fontSize: 7.5, color: 'var(--acc)', transform: 'translateY(0.13em)' }}>
            YOUR CALL
          </span>
          {/* The only words the crew's calls get now. */}
          <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.05em', color: 'var(--gt-muted-2)' }}>
            {card.calledTxt}
          </span>
          <div style={{ flex: 1 }} />
          <span onClick={() => void s.wnSkip()} style={SKIP}>
            SKIP →
          </span>
        </div>
        <VoteButtons style={{ width: '100%' }} onPick={(l) => void s.wnMark(l)} />
        <CallColumns cols={card.callCols} />
      </div>

      {/* BAND 4 · THE NUMBERS ---------------------------------------------- */}
      <NumbersBand nums={card.nums} />

    </Modal>
  );
}
